import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useProfileCompletion } from "../hooks/useProfileCompletion";
import invoiceV2ApiService, {
  InvoiceV2Request,
} from "../services/invoice-v2-api";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import InvoiceV2Dialog from "../components/Invoice/InvoiceV2Dialog";
import PdfViewer from "../components/shared/PdfViewer";
import ImageViewer from "../components/shared/ImageViewer";
import { getCurrencyByCode } from "../utils/currencyData";

const InvoiceV2Page: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const { isComplete: isProfileComplete } = useProfileCompletion();
  const [requests, setRequests] = useState<InvoiceV2Request[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<
    "FILE_UPLOAD" | "CREATE_REQUEST"
  >("FILE_UPLOAD");
  const [editingRequest, setEditingRequest] = useState<InvoiceV2Request | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string>("");
  const [selectedPdfFileName, setSelectedPdfFileName] = useState<string>("");
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>("");
  const [selectedImageFileName, setSelectedImageFileName] =
    useState<string>("");
  const [viewableFiles, setViewableFiles] = useState<Record<string, boolean>>(
    {},
  );

  // Load requests on component mount
  useEffect(() => {
    loadRequests();
  }, []);

  const isFileViewable = (fileName: string): boolean => {
    if (!fileName) return false;
    const extension = fileName.toLowerCase().split(".").pop();
    // Allow PDF, JPEG, and PNG files to be viewed. Disable only for ZIP
    return (
      extension === "pdf" ||
      extension === "jpg" ||
      extension === "jpeg" ||
      extension === "png"
    );
  };

  const checkFileViewability = async (requests: InvoiceV2Request[]) => {
    const viewability: Record<string, boolean> = {};
    for (const request of requests) {
      try {
        const metadata = await invoiceV2ApiService.getAttachmentMetadata(
          request.requestId || "",
        );
        viewability[request.requestId || ""] = isFileViewable(
          metadata.fileName,
        );
      } catch (err) {
        // If we can't get metadata, assume not viewable
        viewability[request.requestId || ""] = false;
      }
    }
    setViewableFiles(viewability);
  };

  const loadRequests = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const data = await invoiceV2ApiService.getInvoiceRequestsByEmployee(
        user.id,
      );
      // Sort by submission date (latest first)
      const sortedData = data.sort((a, b) => {
        const dateA = new Date(a.submissionDate).getTime();
        const dateB = new Date(b.submissionDate).getTime();
        return dateB - dateA;
      });
      setRequests(sortedData);
      await checkFileViewability(sortedData);
      setError(undefined);
    } catch (err) {
      setError(t("error.failedToLoadRequests"));
      console.error(t("error.failedToLoadRequests"), err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (request: InvoiceV2Request) => {
    // Check if profile is complete before allowing edit
    if (!isProfileComplete) {
      setError(t("profile.incompleteWarning"));
      return;
    }

    setEditingRequest(request);
    setDialogMode(
      request.submissionType === "FILE_UPLOAD"
        ? "FILE_UPLOAD"
        : "CREATE_REQUEST",
    );
    setOpenDialog(true);
  };

  const handleDeleteClick = (requestId: string) => {
    setRequestToDelete(requestId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!requestToDelete) return;
    try {
      await invoiceV2ApiService.deleteInvoiceRequest(
        requestToDelete,
        user?.username || "",
      );
      setSuccess(t("success.requestDeleted"));
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
      await loadRequests();
    } catch (err) {
      setError(t("error.failedToDeleteRequest"));
      console.error(t("error.failedToDeleteRequest"), err);
    }
  };

  const handleViewFile = async (requestId: string) => {
    try {
      // Get the file metadata to determine file type
      const metadata =
        await invoiceV2ApiService.getAttachmentMetadata(requestId);
      const fileUrl = `/ofc/invoice/v2/download/${requestId}`;
      const extension = metadata.fileName.toLowerCase().split(".").pop();

      if (extension === "pdf") {
        // Open PDF in PDF viewer
        setSelectedPdfUrl(fileUrl);
        setSelectedPdfFileName(metadata.fileName);
        setPdfViewerOpen(true);
      } else if (["jpg", "jpeg", "png"].includes(extension || "")) {
        // Open image in image viewer
        setSelectedImageUrl(fileUrl);
        setSelectedImageFileName(metadata.fileName);
        setImageViewerOpen(true);
      }
    } catch (err) {
      setError(t("error.failedToViewAttachment"));
      console.error(t("error.failedToViewAttachment"), err);
    }
  };

  const handleDownload = async (requestId: string) => {
    try {
      // Get the original filename first
      const metadata =
        await invoiceV2ApiService.getAttachmentMetadata(requestId);
      const blob = await invoiceV2ApiService.downloadAttachment(requestId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Use the original filename from metadata
      link.download = metadata.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t("error.failedToDownloadAttachment"));
      console.error(t("error.failedToDownloadAttachment"), err);
    }
  };

  const handleDownloadOptionalAttachment = async (requestId: string) => {
    try {
      // Get the original filename first
      const metadata =
        await invoiceV2ApiService.getOptionalAttachmentMetadata(requestId);
      const blob =
        await invoiceV2ApiService.downloadOptionalAttachment(requestId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = metadata.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t("error.failedToDownloadAttachment"));
      console.error(t("error.failedToDownloadAttachment"), err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SUBMITTED":
        return "info";
      case "PROCESSING":
        return "warning";
      case "PROCESSED":
        return "success";
      case "REJECTED":
        return "error";
      default:
        return "default";
    }
  };

  const getSubmissionTypeLabel = (type: string) => {
    return type === "FILE_UPLOAD"
      ? t("invoice.v2.fileUpload")
      : t("invoice.v2.createRequest");
  };

  return (
    <Container maxWidth="xl" sx={{ py: 1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">{t("invoice.v2.title")}</Typography>
        {!isMobile && (
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingRequest(null);
                setDialogMode("CREATE_REQUEST");
                setOpenDialog(true);
              }}
              disabled={!isProfileComplete}
              title={!isProfileComplete ? t("profile.incompleteWarning") : ""}
            >
              {t("invoice.v2.newInvoice")}
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => {
                setEditingRequest(null);
                setDialogMode("FILE_UPLOAD");
                setOpenDialog(true);
              }}
              disabled={!isProfileComplete}
              title={!isProfileComplete ? t("profile.incompleteWarning") : ""}
            >
              {t("invoice.v2.uploadReceipt")}
            </Button>
          </Box>
        )}
      </Box>

      {!isProfileComplete && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("profile.incompleteWarning")}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError(undefined)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(undefined)}
        >
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        /* Mobile Card View */
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {requests.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography color="textSecondary">
                {t("invoice.v2.noRequests")}
              </Typography>
            </Paper>
          ) : (
            requests.map((request) => (
              <Paper key={request.id} variant="outlined" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("invoice.v2.requestId")}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {request.requestId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      label={request.status}
                      color={getStatusColor(request.status || "")}
                      size="small"
                    />
                    {request.status === "REJECTED" &&
                      request.rejectionComment && (
                        <Tooltip title={request.rejectionComment}>
                          <InfoIcon
                            sx={{
                              fontSize: 18,
                              color: "error.main",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      )}
                  </Box>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("invoice.v2.type")}
                  </Typography>
                  <Typography variant="body2">
                    {getSubmissionTypeLabel(request.submissionType)}
                  </Typography>
                </Box>

                {request.submissionType === "FILE_UPLOAD" &&
                  request.attachmentFilePath && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("invoice.v2.filename")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          wordBreak: "break-word",
                          fontSize: "0.85rem",
                        }}
                      >
                        {request.attachmentFilePath}
                      </Typography>
                    </Box>
                  )}

                {request.submissionType === "FILE_UPLOAD" && request.notes && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("invoice.v2.notes")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: "break-word",
                        fontSize: "0.85rem",
                      }}
                    >
                      {request.notes}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("invoice.v2.date")}
                    </Typography>
                    <Typography variant="body2">
                      {request.createdAt
                        ? new Date(
                            parseInt(request.createdAt) * 1000,
                          ).toLocaleDateString()
                        : "-"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t("invoice.v2.amount")}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="primary"
                    >
                      {request.totalAmount > 0
                        ? (() => {
                            const currency =
                              request.items?.[0]?.currency || "JPY";
                            const currencyInfo = getCurrencyByCode(currency);
                            const symbol = currencyInfo?.symbol || currency;
                            return `${symbol}${request.totalAmount.toLocaleString()}`;
                          })()
                        : "-"}
                    </Typography>
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    justifyContent: "flex-end",
                    borderTop: 1,
                    borderColor: "divider",
                    pt: 1.5,
                  }}
                >
                  {request.submissionType === "CREATE_REQUEST" && (
                    <Tooltip
                      title={
                        viewableFiles[request.requestId || ""]
                          ? t("common.view")
                          : t("invoice.v2.viewNotAvailable")
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleViewFile(request.requestId || "")
                          }
                          color="primary"
                          disabled={!viewableFiles[request.requestId || ""]}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Edit Button - Only for SUBMITTED or REJECTED status */}
                  {(request.status === "SUBMITTED" ||
                    request.status === "REJECTED") && (
                    <Tooltip title={t("common.edit")}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditClick(request)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Delete Button - Only for SUBMITTED or REJECTED status */}
                  {(request.status === "SUBMITTED" ||
                    request.status === "REJECTED") && (
                    <Tooltip title={t("common.delete")}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          handleDeleteClick(request.requestId || "")
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Paper>
            ))
          )}
        </Box>
      ) : (
        /* Desktop Table View */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("invoice.v2.requestId")}</TableCell>
                <TableCell>{t("invoice.v2.type")}</TableCell>
                <TableCell>{t("invoice.v2.filename")}</TableCell>
                <TableCell>{t("invoice.v2.notes")}</TableCell>
                <TableCell>{t("invoice.v2.date")}</TableCell>
                <TableCell align="center">{t("invoice.v2.amount")}</TableCell>
                <TableCell>{t("invoice.v2.status")}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography color="textSecondary">
                      {t("invoice.v2.noRequests")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.requestId}</TableCell>
                    <TableCell>
                      {getSubmissionTypeLabel(request.submissionType)}
                    </TableCell>
                    <TableCell>
                      {request.submissionType === "FILE_UPLOAD" &&
                      request.attachmentFilePath
                        ? request.attachmentFilePath
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {request.submissionType === "FILE_UPLOAD" && request.notes
                        ? request.notes
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {request.createdAt
                        ? new Date(
                            parseInt(request.createdAt) * 1000,
                          ).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell align="center">
                      {request.totalAmount > 0
                        ? (() => {
                            const currency =
                              request.items?.[0]?.currency || "JPY";
                            const currencyInfo = getCurrencyByCode(currency);
                            const symbol = currencyInfo?.symbol || currency;
                            return `${symbol}${request.totalAmount.toLocaleString()}`;
                          })()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Chip
                          label={request.status}
                          color={getStatusColor(request.status || "")}
                          size="small"
                        />
                        {request.status === "REJECTED" &&
                          request.rejectionComment && (
                            <Tooltip title={request.rejectionComment}>
                              <InfoIcon
                                sx={{
                                  fontSize: 18,
                                  color: "error.main",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {/* View PDF Button */}
                      {request.submissionType === "CREATE_REQUEST" && (
                        <Tooltip
                          title={
                            viewableFiles[request.requestId || ""]
                              ? t("common.view")
                              : t("invoice.v2.viewNotAvailable")
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleViewFile(request.requestId || "")
                              }
                              color="primary"
                              disabled={!viewableFiles[request.requestId || ""]}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {/* Download PDF Button for CREATE_REQUEST */}
                      {request.submissionType === "CREATE_REQUEST" && (
                        <Tooltip title={t("invoice.v2.downloadPdf")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(request.requestId || "")
                            }
                            color="primary"
                          >
                            <PictureAsPdfIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Download File Button for FILE_UPLOAD */}
                      {request.submissionType === "FILE_UPLOAD" && (
                        <Tooltip title={t("invoice.v2.downloadFile")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(request.requestId || "")
                            }
                            color="primary"
                          >
                            <AttachFileIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Download Optional Attachment Button */}
                      {request.submissionType === "CREATE_REQUEST" &&
                        request.optionalAttachmentBlobId && (
                          <Tooltip
                            title={t("invoice.v2.downloadOptionalAttachment")}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadOptionalAttachment(
                                  request.requestId || "",
                                )
                              }
                              color="primary"
                            >
                              <AttachFileIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                      {/* Edit Button - Only for SUBMITTED or REJECTED status */}
                      {(request.status === "SUBMITTED" ||
                        request.status === "REJECTED") && (
                        <Tooltip title={t("common.edit")}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditClick(request)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Delete Button */}
                      {(request.status === "SUBMITTED" ||
                        request.status === "REJECTED") && (
                        <Tooltip title={t("common.delete")}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              handleDeleteClick(request.requestId || "")
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("common.confirmDelete")}</DialogTitle>
        <DialogContent>{t("invoice.v2.deleteConfirmation")}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice V2 Dialog for both FILE_UPLOAD and CREATE_REQUEST */}
      <InvoiceV2Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setEditingRequest(null);
        }}
        submissionType={dialogMode}
        editingRequest={editingRequest}
        onSuccess={() => {
          setOpenDialog(false);
          setEditingRequest(null);
          loadRequests();
        }}
      />

      {/* PDF Viewer Modal */}
      <PdfViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={selectedPdfUrl}
        fileName={selectedPdfFileName}
      />

      {/* Image Viewer Modal */}
      <ImageViewer
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        imageUrl={selectedImageUrl}
        fileName={selectedImageFileName}
      />
    </Container>
  );
};

export default InvoiceV2Page;
